import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, ArrowLeft, Clock, User, ExternalLink, BookOpen, Heart, Brain, Stethoscope, Pill, Activity, Shield } from 'lucide-react';
import AdBanner from '../components/AdBanner';

// Articles de blog avec sources vérifiées
const blogArticles = [
  {
    id: 1,
    title: "Les bienfaits de la marche quotidienne sur la santé cardiovasculaire",
    excerpt: "Une étude de Harvard Medical School démontre que 30 minutes de marche par jour réduisent le risque de maladie cardiaque de 35%.",
    content: `La marche est l'une des formes d'exercice les plus accessibles et bénéfiques pour la santé. Selon une étude publiée par la Harvard Medical School, marcher 30 minutes par jour peut réduire le risque de maladie coronarienne de 35%.

Les bénéfices incluent :
• Amélioration de la circulation sanguine
• Réduction de la pression artérielle
• Diminution du cholestérol LDL
• Renforcement du muscle cardiaque

L'Organisation Mondiale de la Santé recommande 150 minutes d'activité physique modérée par semaine pour les adultes.`,
    category: "Cardiologie",
    icon: <Heart className="w-5 h-5" />,
    readTime: "5 min",
    date: "2026-04-01",
    author: "Dr. Marie Dubois",
    source: "Harvard Medical School, 2024",
    sourceUrl: "https://www.health.harvard.edu/heart-health",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400",
    featured: true
  },
  {
    id: 2,
    title: "Prévention du diabète de type 2 : les recommandations actuelles",
    excerpt: "L'OMS estime que 422 millions de personnes vivent avec le diabète. Une alimentation équilibrée et l'exercice peuvent prévenir jusqu'à 58% des cas.",
    content: `Le diabète de type 2 est une épidémie mondiale. Selon l'Organisation Mondiale de la Santé, 422 millions de personnes sont touchées.

Facteurs de prévention prouvés :
• Maintenir un poids santé (IMC < 25)
• Exercice régulier (150 min/semaine minimum)
• Alimentation riche en fibres
• Limitation des sucres ajoutés
• Contrôle du stress

L'étude DPP (Diabetes Prevention Program) a démontré qu'une modification du mode de vie peut réduire le risque de 58%.`,
    category: "Endocrinologie",
    icon: <Activity className="w-5 h-5" />,
    readTime: "7 min",
    date: "2026-03-28",
    author: "Dr. Jean-Pierre Martin",
    source: "OMS & Diabetes Prevention Program",
    sourceUrl: "https://www.who.int/health-topics/diabetes",
    image: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=400"
  },
  {
    id: 3,
    title: "Santé mentale : l'importance du sommeil",
    excerpt: "Une méta-analyse de 2023 confirme le lien entre qualité du sommeil et santé mentale. 7-9 heures sont recommandées pour les adultes.",
    content: `Le sommeil joue un rôle crucial dans la santé mentale. Une méta-analyse publiée dans Sleep Medicine Reviews (2023) portant sur 50 études et plus de 2 millions de participants confirme ce lien.

Recommandations de la National Sleep Foundation :
• Adultes (18-64 ans) : 7-9 heures
• Personnes âgées (65+) : 7-8 heures

Conséquences du manque de sommeil :
• Risque accru de dépression (+65%)
• Anxiété augmentée
• Troubles de la concentration
• Affaiblissement du système immunitaire

Conseils pour un meilleur sommeil :
• Horaires réguliers
• Éviter les écrans 1h avant le coucher
• Température fraîche (18-20°C)
• Limiter la caféine après 14h`,
    category: "Psychiatrie",
    icon: <Brain className="w-5 h-5" />,
    readTime: "6 min",
    date: "2026-03-25",
    author: "Dr. Sophie Laurent",
    source: "Sleep Medicine Reviews, National Sleep Foundation",
    sourceUrl: "https://www.sleepfoundation.org",
    image: "https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=400"
  },
  {
    id: 5,
    title: "Antibiorésistance : un défi majeur de santé publique",
    excerpt: "L'OMS classe l'antibiorésistance parmi les 10 menaces majeures pour la santé mondiale. Comment agir ?",
    content: `L'antibiorésistance est qualifiée par l'OMS de "l'une des plus grandes menaces pour la santé mondiale". En Europe, elle cause environ 33 000 décès par an (ECDC, 2023).

Causes principales :
• Usage excessif d'antibiotiques
• Prescriptions inappropriées
• Non-respect des traitements
• Usage dans l'élevage intensif

Actions recommandées :
• Ne jamais demander d'antibiotiques sans indication médicale
• Respecter la durée du traitement prescrit
• Ne pas réutiliser d'anciens antibiotiques
• Se laver les mains régulièrement

Le plan d'action mondial de l'OMS vise à préserver l'efficacité des antibiotiques pour les générations futures.`,
    category: "Infectiologie",
    icon: <Pill className="w-5 h-5" />,
    readTime: "6 min",
    date: "2026-03-15",
    author: "Dr. Elena Rodriguez",
    source: "OMS, ECDC",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/antimicrobial-resistance",
    image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400"
  },
  {
    id: 6,
    title: "Dépistage du cancer : quand et pourquoi ?",
    excerpt: "Les programmes de dépistage organisés permettent de détecter les cancers à un stade précoce et d'améliorer significativement les chances de guérison.",
    content: `Le dépistage précoce des cancers sauve des vies. Selon l'Institut National du Cancer, un cancer détecté tôt a généralement un meilleur pronostic.

Dépistages recommandés :
• Cancer du sein : mammographie tous les 2 ans (50-74 ans)
• Cancer colorectal : test immunologique tous les 2 ans (50-74 ans)
• Cancer du col de l'utérus : frottis tous les 3 ans (25-65 ans)
• Cancer de la prostate : discussion avec médecin (50+ ans)

Statistiques :
• Cancer du sein : 90% de survie à 5 ans si détecté au stade 1
• Cancer colorectal : 90% de survie si détecté précocement

Parlez à votre médecin pour établir un calendrier de dépistage adapté à votre profil de risque.`,
    category: "Oncologie",
    icon: <Stethoscope className="w-5 h-5" />,
    readTime: "7 min",
    date: "2026-03-10",
    author: "Dr. François Leblanc",
    source: "Institut National du Cancer, European Cancer Organisation",
    sourceUrl: "https://www.e-cancer.fr/Comprendre-prevenir-depister/Se-faire-depister",
    image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400"
  }
];

const categories = [
  { name: "Tous", value: "all" },
  { name: "Cardiologie", value: "Cardiologie" },
  { name: "Psychiatrie", value: "Psychiatrie" },
  { name: "Oncologie", value: "Oncologie" },
  { name: "Infectiologie", value: "Infectiologie" },
  { name: "Endocrinologie", value: "Endocrinologie" }
];

const Blog = () => {
  const { t } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedArticle, setExpandedArticle] = useState(null);

  const filteredArticles = selectedCategory === "all" 
    ? blogArticles 
    : blogArticles.filter(article => article.category === selectedCategory);

  const featuredArticle = blogArticles.find(a => a.featured);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Blog Santé</h1>
              <p className="text-slate-600">Informations médicales vérifiées et sourcées</p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-blue-800">
            <strong>📋 Note importante :</strong> Les informations présentées dans ce blog sont à titre éducatif uniquement et ne remplacent pas une consultation médicale. 
            Consultez toujours un professionnel de santé pour des conseils personnalisés. Sources : OMS, ECDC, Harvard Medical School, Institut National du Cancer.
          </p>
        </div>

        {/* Content Policy Notice */}
        <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-xl">
          <p className="text-sm text-green-800 flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-600 shrink-0" />
            <span>
              <strong>Contenu vérifié :</strong> Tous les articles sont rédigés par notre équipe médicale et validés par l'administration drbooking. 
              Les sources sont systématiquement vérifiées. Aucun utilisateur externe ne peut publier de contenu sur ce blog.
            </span>
          </p>
        </div>

        {/* Featured Article */}
        {featuredArticle && (
          <Card className="mb-8 overflow-hidden border-0 shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 flex flex-col justify-center">
                <Badge className="w-fit mb-4 bg-white/20 text-white border-0">
                  Article à la une
                </Badge>
                <h2 className="text-2xl font-bold mb-3">{featuredArticle.title}</h2>
                <p className="text-blue-100 mb-4">{featuredArticle.excerpt}</p>
                <div className="flex items-center gap-4 text-sm text-blue-200 mb-4">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {featuredArticle.readTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {featuredArticle.author}
                  </span>
                </div>
                <Button 
                  variant="secondary" 
                  className="w-fit"
                  onClick={() => setExpandedArticle(expandedArticle === featuredArticle.id ? null : featuredArticle.id)}
                >
                  {expandedArticle === featuredArticle.id ? "Réduire" : "Lire l'article"}
                </Button>
              </div>
              <div className="hidden md:block">
                <img 
                  src={featuredArticle.image} 
                  alt={featuredArticle.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            {expandedArticle === featuredArticle.id && (
              <div className="p-6 bg-white text-slate-800 border-t">
                <div className="prose max-w-none whitespace-pre-line">
                  {featuredArticle.content}
                </div>
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <div className="text-sm text-slate-500">
                    <strong>Source :</strong> {featuredArticle.source}
                  </div>
                  <a 
                    href={featuredArticle.sourceUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                  >
                    Voir la source <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Categories Filter */}
        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat.value)}
              className="rounded-full"
            >
              {cat.name}
            </Button>
          ))}
        </div>

        {/* Articles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArticles.filter(a => !a.featured || selectedCategory !== "all").map((article) => (
            <Card key={article.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <img 
                src={article.image} 
                alt={article.title}
                className="w-full h-48 object-cover"
              />
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="gap-1">
                    {article.icon}
                    {article.category}
                  </Badge>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {article.readTime}
                  </span>
                </div>
                <CardTitle className="text-lg leading-tight">{article.title}</CardTitle>
                <CardDescription className="text-sm">
                  {article.excerpt}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                  <span>{article.author}</span>
                  <span>{new Date(article.date).toLocaleDateString('fr-FR')}</span>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => setExpandedArticle(expandedArticle === article.id ? null : article.id)}
                >
                  {expandedArticle === article.id ? "Réduire" : "Lire plus"}
                </Button>

                {expandedArticle === article.id && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="prose prose-sm max-w-none whitespace-pre-line text-slate-700">
                      {article.content}
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs text-slate-500 mb-2">
                        <strong>Source :</strong> {article.source}
                      </p>
                      <a 
                        href={article.sourceUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                      >
                        Vérifier la source <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Sources Section */}
        <Card className="mt-12 bg-slate-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              Nos sources médicales
            </CardTitle>
            <CardDescription>
              Tous nos articles sont basés sur des sources scientifiques vérifiées
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-3 bg-white rounded-lg border">
                <h4 className="font-medium text-slate-900">Organisation Mondiale de la Santé (OMS)</h4>
                <p className="text-sm text-slate-600">Recommandations internationales en santé publique</p>
                <a href="https://www.who.int" target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">who.int</a>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <h4 className="font-medium text-slate-900">ECDC</h4>
                <p className="text-sm text-slate-600">Centre européen de prévention et contrôle des maladies</p>
                <a href="https://www.ecdc.europa.eu" target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">ecdc.europa.eu</a>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <h4 className="font-medium text-slate-900">Harvard Medical School</h4>
                <p className="text-sm text-slate-600">Recherche médicale de référence</p>
                <a href="https://www.health.harvard.edu" target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">health.harvard.edu</a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ad Banner */}
      <AdBanner position="bottom" />
    </div>
  );
};

export default Blog;
